import React, { useEffect, useState } from "react";
import api, { formatIDR } from "../lib/api";
import { Copy, Monitor, Bot, X, StopCircle } from "lucide-react";
import { toast } from "sonner";

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);
const waPhone = (p) => {
  const c = (p || "").replace(/\D/g, "");
  return c.startsWith("0") ? "62" + c.slice(1) : c;
};
const SEND_CLS = { terkirim: "text-[#059669]", antri: "text-[#B45309]", gagal: "text-[#DC2626]" };
const fmtDate = (d) => {
  if (!d) return "-";
  const [y, m, dd] = d.split("-");
  return `${dd}-${m}-${y}`;
};
const STATUS_LABEL = { active: "Aktif", paid: "Selesai — sudah Lunas", done: "Selesai — lewat tanggal berakhir", stopped: "Dihentikan" };

// Popup Tagih (Dashboard → Selesai — Belum Lunas): copy pesan ke WA Desktop, atau penagihan otomatis via AI Masterbocor (WAHA).
const TagihModal = ({ order, onClose, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("manual");
  const [text, setText] = useState("");
  const [times, setTimes] = useState(2);
  const [endDate, setEndDate] = useState(addDays(7));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/billing-reminders/preview", { params: { order_id: order.id } });
      setInfo(data);
      setText(data.manual_text || "");
      if (data.active) { setMode("ai"); setTimes(data.active.times_per_day); setEndDate(data.active.end_date); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat data tagihan"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order.id]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Pesan disalin"); } catch (_) { toast.error("Gagal menyalin — salin manual dari kotak pesan"); }
  };
  const openDesktop = async () => {
    try { await navigator.clipboard.writeText(text); } catch (_) { /* noop */ }
    window.location.href = `whatsapp://send?phone=${waPhone(info?.phone || order.customer_phone)}&text=${encodeURIComponent(text)}`;
    toast.success("Pesan disalin & WhatsApp Desktop dibuka");
  };

  const startAi = async () => {
    setBusy(true);
    try {
      await api.post("/billing-reminders", { order_id: order.id, times_per_day: Number(times), end_date: endDate });
      toast.success(`Penagihan AI aktif: ${times}x sehari sampai ${endDate}`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengaktifkan penagihan"); } finally { setBusy(false); }
  };
  const stopAi = async () => {
    if (!window.confirm("Hentikan penagihan otomatis untuk order ini?")) return;
    setBusy(true);
    try {
      await api.post(`/billing-reminders/${info.active.id}/stop`);
      toast.success("Penagihan dihentikan");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };

  const act = info?.active;
  const last = !act && info?.last;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] w-full max-w-xl max-h-[92vh] overflow-y-auto" data-testid="tagih-modal">
        <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-start justify-between gap-3">
          <div>
            <div className="label-cap">Tagih Pembayaran</div>
            <div className="font-heading text-lg font-bold">{order.customer_name || "-"} · <span className="font-mono">{order.order_no}</span></div>
            <div className="text-xs text-[#52525B]">{order.payment_status}{order.dp_amount > 0 ? ` · DP ${formatIDR(order.dp_amount)}` : ""} · sisa <b className="text-[#DC2626]">{formatIDR(order.sisa)}</b> · {info?.phone || order.customer_phone}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F4F5]"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            {[["manual", "Copy ke WA Desktop", Monitor], ["ai", "Tagih via AI Masterbocor", Bot]].map(([k, l, Ic]) => (
              <button key={k} type="button" onClick={() => setMode(k)} data-testid={`tagih-mode-${k}`}
                className={`flex-1 px-3 py-2 text-xs font-bold uppercase border flex items-center justify-center gap-2 ${mode === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#E4E4E7] bg-white"}`}>
                <Ic size={14} /> {l}
              </button>
            ))}
          </div>

          {!info && <div className="text-sm text-[#52525B]">Memuat…</div>}

          {info && mode === "manual" && (
            <>
              <div>
                <label className="label-cap block mb-1.5">Pesan (bisa diedit)</label>
                <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} className={ic} data-testid="tagih-text" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={copy} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7] flex items-center gap-2" data-testid="tagih-copy"><Copy size={14} /> Copy Pesan</button>
                <button onClick={openDesktop} className="px-4 py-2 text-xs font-bold uppercase bg-[#059669] text-white flex items-center gap-2" data-testid="tagih-open-desktop"><Monitor size={14} /> Copy & Buka WA Desktop</button>
              </div>
              <p className="text-[11px] text-[#52525B]">Pesan disalin ke clipboard lalu WhatsApp Desktop terbuka di chat pelanggan dengan pesan terisi — tinggal tekan kirim.</p>
            </>
          )}

          {info && mode === "ai" && (
            <>
              {act ? (
                <div className="border border-[#059669]/40 bg-[#059669]/5 p-3 text-xs space-y-1" data-testid="tagih-ai-active">
                  <div className="font-bold text-[#059669] uppercase tracking-wider text-[10px]">Penagihan AI aktif</div>
                  <div>{act.times_per_day}x sehari · berakhir {act.end_date} · sudah dikirim {act.sent_count || 0}x</div>
                  {act.next_today && <div>Jadwal berikutnya hari ini sekitar {act.next_today}</div>}
                  <div className="text-[#52525B]">Berhenti otomatis bila order Lunas.</div>
                </div>
              ) : last ? (
                <div className="border border-[#E4E4E7] p-3 text-xs text-[#52525B]">
                  Penagihan terakhir: {STATUS_LABEL[last.status] || last.status} · terkirim {last.sent_count || 0}x{last.ended_reason ? ` · ${last.ended_reason}` : ""}
                </div>
              ) : null}
              {(info.history?.sends?.length > 0 || info.history?.upcoming?.length > 0) && (
                <div data-testid="tagih-ai-history">
                  <label className="label-cap block mb-1.5">
                    Pengiriman WA ({info.history.sends.filter((x) => x.status === "terkirim").length} terkirim dari {info.history.sends.length})
                  </label>
                  <div className="border border-[#E4E4E7] max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#FAFAFA]">
                        <tr className="border-b border-[#E4E4E7]">
                          <th className="text-left px-3 py-1.5 label-cap">#</th>
                          <th className="text-left px-3 py-1.5 label-cap">Tanggal</th>
                          <th className="text-left px-3 py-1.5 label-cap">Jam Jadwal</th>
                          <th className="text-left px-3 py-1.5 label-cap">Terkirim</th>
                          <th className="text-left px-3 py-1.5 label-cap">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...info.history.sends].reverse().map((x) => (
                          <tr key={`s${x.no}`} className="border-b border-[#E4E4E7] last:border-b-0">
                            <td className="px-3 py-1.5 font-mono">{x.no}</td>
                            <td className="px-3 py-1.5 font-mono">{fmtDate(x.date)}</td>
                            <td className="px-3 py-1.5 font-mono">{x.slot || "-"}</td>
                            <td className="px-3 py-1.5 font-mono">{x.sent_time || "-"}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-[10px] font-bold uppercase ${SEND_CLS[x.status] || "text-[#52525B]"}`}>{x.status === "antri" ? "Antri" : x.status === "terkirim" ? "Terkirim" : x.status === "gagal" ? "Gagal" : x.status}</span>
                              {x.error && <div className="text-[10px] text-[#DC2626] truncate max-w-[160px]" title={x.error}>{x.error}</div>}
                            </td>
                          </tr>
                        ))}
                        {(info.history.upcoming || []).map((x) => (
                          <tr key={`u${x.slot}`} className="border-b border-[#E4E4E7] last:border-b-0 bg-[#002FA7]/5">
                            <td className="px-3 py-1.5 font-mono text-[#52525B]">—</td>
                            <td className="px-3 py-1.5 font-mono">{fmtDate(x.date)}</td>
                            <td className="px-3 py-1.5 font-mono">±{x.slot}</td>
                            <td className="px-3 py-1.5 font-mono">-</td>
                            <td className="px-3 py-1.5"><span className="text-[10px] font-bold uppercase text-[#002FA7]">Terjadwal</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-[#52525B] mt-1">Jam hari berikutnya diacak ulang setiap pagi. "Antri" = sudah masuk antrian WA, menunggu giliran kirim.</p>
                </div>
              )}
              {!info.wa_gateway_enabled &&<div className="text-xs text-[#DC2626] font-bold">WA Gateway (WAHA) belum aktif di Pengaturan — penagihan AI tidak bisa dijalankan.</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-cap block mb-1.5">Kirim per Hari</label>
                  <select value={times} onChange={(e) => setTimes(e.target.value)} className={ic} data-testid="tagih-times">
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}x sehari</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-cap block mb-1.5">Berakhir Tanggal</label>
                  <input type="date" min={today()} value={endDate} onChange={(e) => setEndDate(e.target.value)} className={ic + " font-mono"} data-testid="tagih-end-date" />
                </div>
              </div>
              <div>
                <label className="label-cap block mb-1.5">Contoh Pesan (berubah tiap kirim)</label>
                <div className="border border-[#E4E4E7] bg-[#FAFAFA] p-3 text-xs whitespace-pre-wrap" data-testid="tagih-ai-sample">{info.ai_sample}</div>
              </div>
              <p className="text-[11px] text-[#52525B]">
                Dikirim via WAHA di jam acak antara pukul {info.send_window?.[0]}–{info.send_window?.[1]}, kalimat & urutan diacak tiap kirim,
                lewat antrian WA (jeda acak & batas harian) supaya natural. Berhenti otomatis bila order sudah Lunas atau lewat tanggal berakhir.
              </p>
              <div className="flex gap-2 justify-end flex-wrap">
                {act && <button onClick={stopAi} disabled={busy} className="px-4 py-2 text-xs font-bold uppercase border border-[#DC2626] text-[#DC2626] flex items-center gap-2 disabled:opacity-50" data-testid="tagih-ai-stop"><StopCircle size={14} /> Hentikan</button>}
                <button onClick={startAi} disabled={busy || !info.wa_gateway_enabled} className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white flex items-center gap-2 disabled:opacity-50" data-testid="tagih-ai-start">
                  <Bot size={14} /> {act ? "Simpan Jadwal Baru" : "Mulai Penagihan"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ic = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white";

export default TagihModal;
